import type { BroadcastEnv } from "src/types/broadcasts";
import { getBroadcastEnvInfo } from "./notificationRoutes";

/**
 * Single choke point for direct iv3api HTTP calls. Every request flows through
 * `iv3Request`, which:
 *   - resolves the base URL for the given env,
 *   - attaches the operator bearer token on authenticated calls,
 *   - and, when an authenticated call is rejected for auth reasons (HTTP 401,
 *     or 429 which iv3api returns for an expired token), transparently
 *     refreshes the token once and retries (the "interceptor" behaviour).
 *
 * Auth handlers are registered by the operator-auth store via `configureIv3Auth`
 * to avoid a hard import cycle (store → service → client → store).
 */

function trimBase(url: string): string {
  return url.replace(/\/$/, "");
}

/**
 * Base URL for loginapi calls (operator OTP — no auth header).
 *
 * `REACT_APP_LOGIN_DOMAIN` is optional and used only for the `local` env, when
 * Login is served from a different origin than Iv3. When it is unset, OTP uses
 * the same host as the Iv3 (data) API (`REACT_APP_DOMAIN_LOCAL` for local).
 * Other environments keep their `REACT_APP_DOMAIN_*` host.
 * `REACT_APP_OPERATOR_AUTH_BASE_URL` still overrides the auth host for any env.
 */
export function authBaseUrl(env: BroadcastEnv): string {
  const override = process.env.REACT_APP_OPERATOR_AUTH_BASE_URL?.trim();
  if (override) return trimBase(override);
  if (env === "local") {
    const loginDomain = process.env.REACT_APP_LOGIN_DOMAIN?.trim();
    if (loginDomain) return trimBase(loginDomain);
  }
  return getBroadcastEnvInfo(env).baseUrl;
}

export async function readApiError(res: Response): Promise<string> {
  const text = await res.text();
  if (!text) return `Request failed (${res.status})`;
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    const msg = data.message ?? data.error ?? data.detail;
    if (typeof msg === "string" && msg.trim()) return msg;
  } catch {
    // fall through to raw text
  }
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}

interface Iv3AuthHandlers {
  /** Current operator token, or "" when none. */
  getToken: () => string;
  /** Attempt a silent refresh; resolves true when a new token is in place. */
  refresh: () => Promise<boolean>;
}

let handlers: Iv3AuthHandlers = {
  getToken: () => "",
  refresh: async () => false,
};

export function configureIv3Auth(next: Iv3AuthHandlers): void {
  handlers = next;
}

export interface Iv3RequestOptions extends RequestInit {
  /** When true, send the operator token and enable 401 auto-refresh + retry. */
  auth?: boolean;
  /** Header the token is sent in. Default "Authorization". */
  authHeader?: string;
  /**
   * How the token is formatted in the header:
   *   - "bearer" (default) → `Authorization: Bearer <token>`
   *   - "raw" → header value is the bare token (e.g. X-IVUEIT-AUTH-TOKEN)
   */
  authScheme?: "bearer" | "raw";
  /**
   * Resolve the base URL for `env`. Defaults to the loginapi host
   * (`authBaseUrl`). Iv3 `/api/v2` calls pass the env's data-API domain
   * (`REACT_APP_DOMAIN_*`, including `REACT_APP_DOMAIN_LOCAL`).
   */
  baseUrl?: (env: BroadcastEnv) => string;
}

export async function iv3Request<T>(
  env: BroadcastEnv,
  path: string,
  {
    auth = false,
    authHeader = "Authorization",
    authScheme = "bearer",
    baseUrl = authBaseUrl,
    ...init
  }: Iv3RequestOptions = {},
): Promise<T> {
  const url = `${baseUrl(env)}${path}`;

  const send = () => {
    const headers = new Headers(init.headers);
    if (init.body != null && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (auth) {
      const token = handlers.getToken();
      if (token) {
        headers.set(
          authHeader,
          authScheme === "raw" ? token : `Bearer ${token}`,
        );
      }
    }
    return fetch(url, { ...init, headers });
  };

  let res = await send();

  // Interceptor: one refresh-and-retry for authenticated calls when the token
  // is rejected. iv3api signals an expired token with gRPC ResourceExhausted,
  // which the gateway maps to HTTP 429 (not 401) — so both must trigger refresh.
  if (auth && (res.status === 401 || res.status === 429)) {
    const refreshed = await handlers.refresh();
    if (refreshed) res = await send();
  }

  if (!res.ok) throw new Error(await readApiError(res));
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return { raw: text } as T;
  }
}
